import type { Attachment } from "./types";
import type { BrowserWorkspace } from "./workspace";

// Never open the protected API URL in another browser context: an installed
// web app and Safari may have different sessions. Download with this session.
export async function fetchAttachmentFile(
  attachment: Pick<Attachment, "id" | "name">,
  workspace: BrowserWorkspace,
  signal?: AbortSignal,
): Promise<File> {
  const response = await fetch(`/api/v1/attachments/${encodeURIComponent(attachment.id)}?workspace=${workspace}`, {
    credentials: "same-origin",
    mode: "same-origin",
    redirect: "error",
    cache: "no-store",
    headers: { "X-Support-Workspace": workspace, Accept: "*/*" },
    signal,
  });
  if (!response.ok) {
    const payload = await response.json().catch(() => null) as { error?: { message?: string } } | null;
    throw new Error(response.status === 401
      ? "Your session has expired. Sign in again to open this file."
      : payload?.error?.message || "This file could not be opened. Please try again.");
  }
  const blob = await response.blob();
  // HTML/SVG/XML and unknown formats must never become executable previews.
  const type = blob.type.split(";", 1)[0].trim().toLowerCase();
  const passiveType = /^(?:image\/(?:png|jpeg|gif|webp|avif|bmp)|application\/pdf|text\/(?:plain|csv))$/.test(type);
  return new File([blob], attachment.name, { type: passiveType ? type : "application/octet-stream" });
}

export function isAttachmentImage(file: Blob): boolean {
  return /^image\/(?:png|jpeg|gif|webp|avif|bmp)$/.test(file.type);
}

export function canShareAttachment(file: File): boolean {
  try {
    return typeof navigator.share === "function" && typeof navigator.canShare === "function"
      && navigator.canShare({ files: [file] });
  } catch { return false; }
}
